import { useMemo } from "react";

import type { PublicEvent } from "../events/schemas";
import { toDate } from "./agenda-schedule";
import { ChapterBlocks } from "./ChapterBlocks";
import { chaptersFor, ownStatTiles, statTiles } from "./landing-model";
import type { AgendaItem } from "./schemas";
import { StatBand } from "./StatBand";
import { useLiveAgenda, useTracks } from "./use-event-content";

/**
 * The number band and the chapters of the bold Overview.
 *
 * Each read sits in a component of its own so that it starts only when the
 * page needs it. An event with the agenda module off reads nothing here and
 * shows the band its dates give. The tracks are read only for the computed
 * "Themes" tile, which exists only when the organiser wrote no tiles of their
 * own. Neither a loading nor a failed read shows an error: the Agenda tab
 * reports those, and an error box on the Overview would be noise.
 */
export function OverviewLanding({
  event,
  showStatBand,
  uid,
}: {
  event: PublicEvent;
  showStatBand: boolean;
  uid: string;
}) {
  if (!event.configAgenda) {
    return showStatBand ? (
      <StatBand tiles={statTiles(event, { sessions: 0, themes: 0 })} />
    ) : null;
  }
  return <WithAgenda event={event} showStatBand={showStatBand} uid={uid} />;
}

function WithAgenda(props: {
  event: PublicEvent;
  showStatBand: boolean;
  uid: string;
}) {
  const { event } = props;
  const agenda = useLiveAgenda(event.eventId);
  // The sessions the Agenda tab lists: those with a time.
  const sessions = useMemo(
    () =>
      agenda.status === "success"
        ? agenda.data.filter((item) => toDate(item.startsAt) !== null)
        : [],
    [agenda],
  );
  const needsThemes = props.showStatBand && ownStatTiles(event).length === 0;
  return needsThemes ? (
    <WithThemes {...props} sessions={sessions} />
  ) : (
    <Landing {...props} sessions={sessions} themes={0} />
  );
}

function WithThemes(props: {
  event: PublicEvent;
  showStatBand: boolean;
  uid: string;
  sessions: AgendaItem[];
}) {
  const tracks = useTracks(props.event.eventId);
  return (
    <Landing
      {...props}
      themes={tracks.status === "success" ? tracks.data.length : 0}
    />
  );
}

function Landing({
  event,
  showStatBand,
  uid,
  sessions,
  themes,
}: {
  event: PublicEvent;
  showStatBand: boolean;
  uid: string;
  sessions: AgendaItem[];
  themes: number;
}) {
  const chapters = useMemo(() => chaptersFor(sessions), [sessions]);
  const eventDates = useMemo(
    () => ({ startDate: event.startDate, endDate: event.endDate }),
    [event.startDate, event.endDate],
  );
  return (
    <>
      {showStatBand && (
        <StatBand
          tiles={statTiles(event, { sessions: sessions.length, themes })}
        />
      )}
      <ChapterBlocks
        chapters={chapters}
        sessions={sessions}
        eventId={event.eventId}
        uid={uid}
        timeZone={event.timezone}
        eventDates={eventDates}
      />
    </>
  );
}
